import React, { useState } from "react";
import { Shield, Plus, Info } from "lucide-react";
import Mo3tiButton from "../../layout/Mo3tiButton";
import { apiFetch } from "../../../../../context/AuthContext";

const TiersPanel = ({
  tiers,
  fetchTiers,
  API_BASE_URL,
  clientName,
  workingCompany,
  currentTheme,
  t,
  isDark,
  getAuthHeaders,
}) => {
  const [newTier, setNewTier] = useState({ tierName: "", monthlyPrice: "", description: "" });
  const [savingTier, setSavingTier] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [error, setError] = useState("");

  const styles = {
    container: { display: "flex", flexDirection: "column", gap: "24px" },
    tableContainer: {
      borderRadius: "20px",
      overflow: "hidden",
      border: `1px solid ${currentTheme.colors.border}`,
      background: isDark ? "rgba(255,255,255,0.02)" : "#fff",
    },
    table: { width: "100%", borderCollapse: "collapse" },
    th: {
      padding: "16px 20px",
      textAlign: "left",
      background: isDark ? "rgba(255,255,255,0.05)" : "#f8fafc",
      color: currentTheme.colors.textSecondary,
      fontSize: "13px",
      fontWeight: "700",
      textTransform: "uppercase",
    },
    td: {
      padding: "16px 20px",
      borderTop: `1px solid ${currentTheme.colors.border}`,
      color: currentTheme.colors.textPrimary,
      fontSize: "15px",
    },
    form: {
      padding: "24px",
      borderRadius: "20px",
      background: isDark ? "rgba(255,255,255,0.05)" : "#f8fafc",
      border: `1px solid ${currentTheme.colors.border}`,
      display: "flex",
      flexDirection: "column",
      gap: "20px",
    },
    input: {
      width: "100%",
      padding: "12px 16px",
      borderRadius: "12px",
      border: `1px solid ${currentTheme.colors.border}`,
      background: isDark ? "rgba(15, 23, 42, 0.8)" : "#fff",
      color: currentTheme.colors.textPrimary,
      fontSize: "14px",
      outline: "none",
    },
    label: {
      fontSize: "13px",
      fontWeight: "600",
      color: currentTheme.colors.textSecondary,
      marginBottom: "8px",
      display: "block",
    },
  };

  const handleAddTier = async () => {
    if (!newTier.tierName.trim() || !newTier.monthlyPrice) {
      setError(t("miTierNamePriceRequired"));
      return;
    }
    setSavingTier(true);
    setError("");
    try {
      const r = await apiFetch(`${API_BASE_URL}/payrollX/medical-insurance/tiers`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({
          clientName,
          workingCompany: workingCompany || undefined,
          tierName: newTier.tierName.trim(),
          monthlyPrice: parseFloat(newTier.monthlyPrice),
          description: newTier.description.trim() || undefined,
        }),
      });
      if (!r.ok) throw new Error();
      await fetchTiers();
      setNewTier({ tierName: "", monthlyPrice: "", description: "" });
    } catch (e) {
      setError(t("miNetworkError"));
    } finally {
      setSavingTier(false);
    }
  };

  const handleToggleActive = async (tier) => {
    setTogglingId(tier.id);
    setError("");
    try {
      const r = await apiFetch(`${API_BASE_URL}/payrollX/medical-insurance/tiers/${tier.id}`, {
        method: "PUT",
        headers: getAuthHeaders(),
        body: JSON.stringify({ isActive: !tier.isActive }),
      });
      if (!r.ok) throw new Error();
      await fetchTiers();
    } catch (e) {
      setError(t("miNetworkError"));
    } finally {
      setTogglingId(null);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.tableContainer}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>{t("miTierName")}</th>
              <th style={styles.th}>{t("miMonthlyPriceEgp")}</th>
              <th style={styles.th}>{t("description")}</th>
              <th style={styles.th}>{t("status")}</th>
            </tr>
          </thead>
          <tbody>
            {tiers.length > 0 ? (
              tiers.map((tier) => (
                <tr key={tier.id}>
                  <td style={{ ...styles.td, opacity: tier.isActive ? 1 : 0.5 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                      <Shield size={16} color={currentTheme.colors.primary} />
                      <span style={{ fontWeight: "600" }}>{tier.tierName}</span>
                    </div>
                  </td>
                  <td style={{ ...styles.td, fontWeight: "700", color: currentTheme.colors.success, opacity: tier.isActive ? 1 : 0.5 }}>
                    {tier.monthlyPrice.toLocaleString()} EGP
                  </td>
                  <td style={{ ...styles.td, opacity: tier.isActive ? 1 : 0.5 }}>{tier.description || "—"}</td>
                  <td style={styles.td}>
                    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                      <button
                        type="button"
                        onClick={() => handleToggleActive(tier)}
                        disabled={togglingId === tier.id}
                        title={tier.isActive ? t("miDeactivate") : t("miActivate")}
                        style={{
                          position: "relative",
                          width: "44px",
                          height: "24px",
                          borderRadius: "999px",
                          border: "none",
                          padding: 0,
                          flexShrink: 0,
                          cursor: togglingId === tier.id ? "wait" : "pointer",
                          background: tier.isActive
                            ? currentTheme.colors.success
                            : isDark ? "rgba(255,255,255,0.2)" : "#cbd5e1",
                          transition: "background 0.2s ease",
                        }}
                      >
                        <span
                          style={{
                            position: "absolute",
                            top: "2px",
                            left: tier.isActive ? "22px" : "2px",
                            width: "20px",
                            height: "20px",
                            borderRadius: "50%",
                            background: "#fff",
                            boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
                            transition: "left 0.2s ease",
                          }}
                        />
                      </button>
                      <span
                        style={{
                          fontSize: "13px",
                          fontWeight: "600",
                          color: tier.isActive ? currentTheme.colors.success : currentTheme.colors.textSecondary,
                        }}
                      >
                        {tier.isActive ? t("active") : t("miInactive")}
                      </span>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={4} style={{ ...styles.td, textAlign: "center", padding: "40px", color: currentTheme.colors.textSecondary }}>
                  {t("miNoTiers")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div style={styles.form}>
        <h3 style={{ margin: 0, fontSize: "18px", fontWeight: "700", color: currentTheme.colors.textPrimary }}>{t("miAddTier")}</h3>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(180px, 100%), 1fr))", gap: "16px" }}>
          <div>
            <label style={styles.label}>{t("miTierName")}</label>
            <input
              style={styles.input}
              placeholder={t("miTierNamePlaceholder")}
              value={newTier.tierName}
              onChange={(e) => setNewTier({ ...newTier, tierName: e.target.value })}
            />
          </div>
          <div>
            <label style={styles.label}>{t("miMonthlyPriceEgp")}</label>
            <input
              style={styles.input}
              type="number"
              placeholder="0"
              value={newTier.monthlyPrice}
              onChange={(e) => setNewTier({ ...newTier, monthlyPrice: e.target.value })}
            />
          </div>
          <div>
            <label style={styles.label}>{t("description")}</label>
            <input
              style={styles.input}
              placeholder={t("miDescriptionPlaceholder")}
              value={newTier.description}
              onChange={(e) => setNewTier({ ...newTier, description: e.target.value })}
            />
          </div>
        </div>
        {error && <p style={{ color: currentTheme.colors.danger, fontSize: "13px", margin: 0 }}>{error}</p>}
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Mo3tiButton variant="primary" onClick={handleAddTier} disabled={savingTier} icon={Plus}>
            {savingTier ? t("miSaving") : t("miAddTier")}
          </Mo3tiButton>
        </div>
      </div>
    </div>
  );
};

export default TiersPanel;